/**
 * A write-back step's target and writes (Feature 71, 7.5): the node it writes
 * into, from the targets a save accepts, and each write: a field of the
 * target's output, a part of the step's own result, append or override, and
 * on an append a separator, a blank line when left empty (D50). Fields come
 * from the installed schemas; where a schema is not known, they are typed.
 */
import type { CapabilitySchema, LinkageStep, LinkageWrite } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Inline, Input, Typography } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Select } from '../../ui/Select.js';
import { FIRST, type Draft } from './draft.js';
import { addWrite, allowedTargets, removeWrite, rootsOf, setTarget, setWrite } from './modes.js';
import type { Schemas } from './references.js';

// The platform's own key on a root's result; never a write's field (D29).
const LINKED = 'linked';
const RESULT = 'result.';

/** A schema's output fields as `name · type`, `linked` left out. */
function outputs(schema: CapabilitySchema | undefined): { value: string; label: string }[] {
  return (schema?.output_fields ?? [])
    .filter((f) => f.name !== LINKED)
    .map((f) => ({ value: f.name, label: `${f.name} · ${f.type}` }));
}

/** Whether a separator means anything: a string field, or one whose type is not known. */
function joinsText(schema: CapabilitySchema | undefined, field: string): boolean {
  const type = schema?.output_fields.find((f) => f.name === field)?.type;
  return type === undefined || type === 'string' || type === 'any';
}

/** A field, as a choice from the schema when it is known, typed otherwise. */
function FieldChoice({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  if (options.length === 0) {
    return <Input aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  // A value the schema no longer offers still shows.
  const known = options.some((o) => o.value === value);
  return (
    <Select
      searchable
      aria-label={label}
      value={value}
      onChange={onChange}
      options={
        known || !value ? options : [{ value, label: `${value} · not declared` }, ...options]
      }
    />
  );
}

function WriteRow({
  index,
  write,
  targetFields,
  ownFields,
  joinsText: text,
  onChange,
  onRemove,
}: {
  index: number;
  write: LinkageWrite;
  targetFields: { value: string; label: string }[];
  ownFields: { value: string; label: string }[];
  /** Whether this write's target field is text, which an append joins. */
  joinsText: boolean;
  onChange: (write: LinkageWrite) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const ownPath = write.source.startsWith(RESULT)
    ? write.source.slice(RESULT.length)
    : write.source;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        paddingTop: 9,
        borderTop: '1px solid var(--border-divider)',
      }}
    >
      <Inline gap={7} align="center" justify="space-between" wrap={false}>
        <Typography variant="meta">Write {n}</Typography>
        <SegmentedControl
          items={[
            { value: 'append', label: 'Append' },
            { value: 'override', label: 'Override' },
          ]}
          value={write.op}
          onChange={(op) => {
            // An override joins nothing, so it keeps no separator.
            const { separator: _dropped, ...rest } = write;
            onChange(op === 'append' ? { ...write, op } : { ...rest, op: 'override' });
          }}
        />
      </Inline>
      <FieldChoice
        label={`Write ${n} into`}
        value={write.field}
        options={targetFields}
        onChange={(field) => onChange({ ...write, field })}
      />
      <FieldChoice
        label={`Write ${n} from`}
        value={ownPath}
        options={ownFields}
        onChange={(path) => onChange({ ...write, source: `${RESULT}${path}` })}
      />
      {write.op === 'append' && text && (
        <Input
          aria-label={`Write ${n} separator`}
          placeholder="a blank line"
          value={write.separator ?? ''}
          onChange={(e) => {
            const { separator: _dropped, ...rest } = write;
            onChange(e.target.value === '' ? rest : { ...write, separator: e.target.value });
          }}
        />
      )}
      <Button variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove write ${n}`}>
        Remove
      </Button>
    </div>
  );
}

export function WriteBackEditor({
  draft,
  step,
  schemas,
  onChange,
}: {
  draft: Draft;
  step: LinkageStep;
  schemas: Schemas;
  onChange: (draft: Draft) => void;
}) {
  const writeBack = step.write_back;
  if (!writeBack) return null;
  const root = rootsOf(draft).get(step.name) ?? FIRST;
  const targetCapability =
    writeBack.target === FIRST
      ? draft.firstCapabilityId
      : draft.steps.find((s) => s.name === writeBack.target)?.capability_id;
  const targetSchema = schemas.get(targetCapability ?? '');
  const targetFields = outputs(targetSchema);
  const ownFields = outputs(schemas.get(step.capability_id));
  return (
    <>
      <Field
        id="write-back-target"
        label="Writes into"
        hint="its root, or a step that links straight in"
      >
        <Select
          id="write-back-target"
          value={writeBack.target}
          onChange={(target) => onChange(setTarget(draft, step.name, target))}
          options={allowedTargets(draft, step.name).map((t) => ({
            value: t,
            label: t === root ? `${t} · its root` : t,
          }))}
        />
      </Field>
      {writeBack.fields.length === 0 && (
        <Typography variant="meta">
          No writes yet; the save refuses a write-back without one.
        </Typography>
      )}
      {writeBack.fields.map((write, i) => (
        <WriteRow
          key={i}
          index={i}
          write={write}
          targetFields={targetFields}
          ownFields={ownFields}
          joinsText={joinsText(targetSchema, write.field)}
          onChange={(next) => onChange(setWrite(draft, step.name, i, next))}
          onRemove={() => onChange(removeWrite(draft, step.name, i))}
        />
      ))}
      <Button
        variant="ghost"
        size="sm"
        icon="add"
        onClick={() =>
          onChange(
            addWrite(draft, step.name, {
              field: targetFields[0]?.value ?? '',
              source: `${RESULT}${ownFields[0]?.value ?? ''}`,
              op: 'append',
            }),
          )
        }
      >
        Add a write
      </Button>
    </>
  );
}
